import { MultiSelect, Stack } from "@mantine/core";
import { SimpleBreadcrumbs } from "../../components/SimpleBreadcrumbs";
import { useIntl } from "react-intl";
import { useTRPC } from "../../trpc";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader } from "../../components/Loader";
import { UserCellRenderer } from "../../utils/columns";
import type { ColDef } from "ag-grid-community";
import { AgGridTable } from "../../components/AgGridTable.js";

export const Users = () => {
  const intl = useIntl();
  const trpc = useTRPC();
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);

  const { data: accounts = [] } = useQuery(trpc.getAccounts.queryOptions());
  const { data: accountsUsers = [], isLoading } = useQuery(trpc.getAccountsUsers.queryOptions({
    accountIds: selectedAccounts.map(Number),
  }));

  const users = useMemo(() => accountsUsers.flatMap((a) => {
    return a.users.map(u => ({
      ...u,
      accountName: a.name,
      role: u.role.name,
    }));
  }), [accountsUsers]);

  type User = typeof users[number];

  const columnDefs: ColDef<User>[] = useMemo(() => [
    {
      field: "accountName",
      headerName: intl.formatMessage({ defaultMessage: "Account", id: "column.accountName" }),

    },
    {
      field: "email",
      headerName: intl.formatMessage({ defaultMessage: "Email", id: "column.email" }),
    },
    {
      field: "id",
      headerName: intl.formatMessage({ defaultMessage: "ID", id: "column.id" }),
    },
    {
      field: "name",
      headerName: intl.formatMessage({ defaultMessage: "User", id: "column.user" }),
      cellRenderer: UserCellRenderer,
    },
    {
      field: "createdAt",
      headerName: intl.formatMessage({ defaultMessage: "Created at", id: "column.createdAt" }),
      sortable: true,
      cellDataType: "dateTimeString",
    },
    {
      field: "updatedAt",
      headerName: intl.formatMessage({ defaultMessage: "Updated at", id: "column.updatedAt" }),
      sortable: true,
      cellDataType: "dateTimeString",
    },
    {
      field: "deletedAt",
      headerName: intl.formatMessage({ defaultMessage: "Status", id: "column.status" }),
      valueGetter: params => params.data?.deletedAt
        ? "inactive"
        : "active",
    },
  ], [intl]);

  return (
    <Stack gap={16} h="100%">
      <SimpleBreadcrumbs
        parent={intl.formatMessage({ defaultMessage: "Admin", id: "navigation.admin" })}
        current={intl.formatMessage({ defaultMessage: "Users", id: "navigation.user" })}
        description={intl.formatMessage({ defaultMessage: "See and manage users for costumer account", id: "text.usersDescription" })}
      />
      <MultiSelect
        onChange={setSelectedAccounts}
        value={selectedAccounts}
        label={intl.formatMessage({ defaultMessage: "Select accounts", id: "label.selectAccounts" })}
        data={accounts.map(({ id, name }) => ({ value: id.toString(), label: name }))}
        limit={20}
        searchable
      />
      {isLoading && <Loader />}
      {users.length > 0 && (
        <AgGridTable
          rowData={users}
          columnDefs={columnDefs}
          domLayout="autoHeight"
          autoSizeStrategy={{
            type: "fitGridWidth",
            defaultMinWidth: 100,
          }}
          suppressHorizontalScroll={false}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
          }}
        />
      )}
    </Stack>
  );
};
